import React, { useContext, useMemo, useRef } from 'react';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { createState, useHookstate } from '@hookstate/core';

import {
  IOnboardingGroup,
  IPaginationResultGroups,
} from '@models/OnboardingGroup.model';
import Labels from '../Labels/Labels';
import {
  FieldErrorTypes,
  OnboardingFileContext,
} from '../OnboardingFile/OnboardingFile.query';

import {
  RequestDetailsWrapper,
  DetailsHeader,
  Header,
  LeftHeader,
  HeaderText,
  HeaderSerial,
  Item,
  LineContainer,
  LineText,
  ColumnsFieldsWrapper,
  ColumnsFields,
  ColumnField,
  Assets,
  AssetRow,
  AssetFieldContainer,
  AssetField,
  ErrorContainer,
  LineTextError,
  NoText,
  RequestDetailsErrorContainer,
} from './RequestDetails.styles';

interface IFillOrEmpty {
  group: IOnboardingGroup;
  label: string;
  field: FieldErrorTypes;
  isError?: boolean;
  textError?: string;
}

const FillOrEmpty = ({
  group,
  label,
  isError,
  textError,
  field,
}: IFillOrEmpty) => {
  const {
    errorDialog,
    selectedGroup,
    fieldErrorSelected,
    errorManufacturerDialog,
  } = useContext(OnboardingFileContext);

  if (isError) {
    return (
      <ErrorContainer>
        <LineTextError limitOfLines={1}>{label || '-'}</LineTextError>
        <Labels
          type="error"
          text={textError || ''}
          onClick={e => {
            e.stopPropagation();
            selectedGroup.set(group);
            fieldErrorSelected.set(field);

            if (field === 'manufacturer') {
              errorManufacturerDialog.set(true);
            } else {
              errorDialog.set(true);
            }
          }}
        />
      </ErrorContainer>
    );
  }

  if (!label) {
    return <NoText>Empty</NoText>;
  }

  return (
    <LineContainer>
      <LineText limitOfLines={1}>{label} </LineText>
    </LineContainer>
  );
};

export const requestDetailsState = createState<boolean>(false);

interface IRequestDetailsRaw {
  groupsQuery?: IPaginationResultGroups<IOnboardingGroup>;
  isError: boolean;
}

export const RequestDetailsRaw = ({
  groupsQuery,
  isError,
}: IRequestDetailsRaw) => {
  const requestDetailsColumnsFieldRef = useRef<null | HTMLDivElement>(null);
  const requestDetailsAssetFieldRef = useRef<null | HTMLDivElement>(null);
  const { status } = useContext(OnboardingFileContext);
  const opeDetailsState = useHookstate(requestDetailsState);

  const groups = groupsQuery
    ? groupsQuery.assets.map(pageData => pageData)
    : [];

  const group = groups[0];

  const getTextError = (textError: string | null) => {
    if (textError?.includes('Invalid')) {
      return 'Invalid';
    }
    if (textError?.includes('Not Found')) {
      return 'Not Found';
    }

    return 'Empty';
  };

  const isAccessory =
    group &&
    !group.isNotAccessory &&
    group.accessoryError &&
    ['identification_requested', 'unidentified'].includes(status);

  const columns = useMemo(() => {
    return (
      group && [
        ...Object.keys(group.items[0].equipmentFields || []),
        ...Object.keys(group.items[0].otherFields || []),
      ]
    );
  }, [group]);

  return opeDetailsState.get() && groupsQuery ? (
    <RequestDetailsWrapper>
      <DetailsHeader>
        <LeftHeader>
          <HeaderText>Request Details</HeaderText>
          <HeaderSerial>{group && group.manufacturer}</HeaderSerial>
        </LeftHeader>
        <BlueIcon
          icon="cross"
          color="#8F95B2"
          onClick={() => {
            opeDetailsState.set(false);
          }}
        />
      </DetailsHeader>

      {isError && (
        <RequestDetailsErrorContainer>
          error on loading data
        </RequestDetailsErrorContainer>
      )}

      {groupsQuery.assets.length > 0 ? (
        <Item>
          <Header>
            <FillOrEmpty
              field="manufacturer"
              group={group}
              label={group.manufacturer}
              isError={
                !!group.mfrError &&
                ['unidentified', 'identification_requested'].includes(status) &&
                !isAccessory
              }
              textError={getTextError(group.mfrError)}
            />
            <FillOrEmpty
              field="model"
              group={group}
              label={group.model}
              isError={
                !!group.mpnError &&
                ['unidentified', 'identification_requested'].includes(status) &&
                !isAccessory
              }
              textError={getTextError(group.mpnError)}
            />
            <FillOrEmpty
              field="productName"
              group={group}
              label={group.productName}
              isError={
                !!group.productNameError &&
                ['unidentified', 'identification_requested'].includes(status) &&
                !isAccessory
              }
              textError={getTextError(group?.productNameError)}
            />
            <LineContainer>
              <LineText limitOfLines={1}>{group?.count}</LineText>
            </LineContainer>
          </Header>

          <ColumnsFieldsWrapper>
            <ColumnsFields
              columns={columns.length}
              ref={requestDetailsColumnsFieldRef}
            >
              {columns.map(key => (
                <ColumnField key={key}>{key}</ColumnField>
              ))}
            </ColumnsFields>
          </ColumnsFieldsWrapper>

          <Assets
            ref={requestDetailsAssetFieldRef}
            onScroll={() => {
              if (
                requestDetailsColumnsFieldRef.current &&
                requestDetailsAssetFieldRef.current
              )
                requestDetailsColumnsFieldRef.current?.scrollTo({
                  top: 0,
                  left: requestDetailsAssetFieldRef.current.scrollLeft,
                });
            }}
            className="bscroll"
          >
            {group.items.map(asset => (
              <AssetRow key={asset.id} columns={columns.length}>
                {asset.equipmentFields &&
                  Object.keys(asset.equipmentFields).map(equipmentKey => (
                    <AssetFieldContainer key={equipmentKey}>
                      <AssetField>
                        {asset.equipmentFields[equipmentKey]}
                      </AssetField>
                    </AssetFieldContainer>
                  ))}
                {asset.otherFields &&
                  Object.keys(asset.otherFields).map(otherKey => (
                    <AssetFieldContainer key={otherKey}>
                      <AssetField>{asset.otherFields[otherKey]}</AssetField>
                    </AssetFieldContainer>
                  ))}
              </AssetRow>
            ))}
          </Assets>
        </Item>
      ) : (
        <RequestDetailsErrorContainer>No data</RequestDetailsErrorContainer>
      )}
    </RequestDetailsWrapper>
  ) : null;
};
